import { useState } from 'react';
import { withStory, WithStoryProps } from '@storyofams/storyblok-toolkit';
import { GetStaticProps } from 'next';
import SbEditable from 'storyblok-react';

import { Button, Heading, Mesh, Seo } from '~/components';
import { BannerDynamic } from '~/components/Banners/BannerDynamic';
import { ArticleCard } from '~/components/Press';
import { enhancedStaticProps } from '~/lib';
import { withLayout } from '~/lib/withLayout';

export interface PressProps extends WithStoryProps {}

const Press = ({ story }: PressProps) => {
  const content = story?.content;

  const [articles, setArticles] = useState(
    story?.content?.articles?.slice(3, 7),
  );

  const loadMore = () => {
    setArticles([
      ...articles,
      ...story?.content?.articles?.slice(
        3 + articles?.length,
        3 + articles?.length + 4,
      ),
    ]);
  };

  const firstArticles = story?.content?.articles?.slice(0, 3);

  return (
    <>
      <Seo story={story} />

      <SbEditable content={content}>
        <div className="pb-12 md:pb-16 pt-32">
          <div className="max-w-2xl mx-auto px-4 flex flex-col justify-center items-center relative z-20">
            <Heading
              as="h6"
              variant="h6"
              className="text-center text-base md:text-base"
            >
              {content?.kicker}
            </Heading>
            <Heading
              as="h1"
              variant="h2"
              className="text-center mt-2 md:mt-4 text-5xl !font-medium"
            >
              {content?.title}
            </Heading>
            <p className="text-base text-grey-600 mt-6 text-center">
              {content?.subtext}
            </p>
          </div>
          <div className="relative h-20 md:h-40 flex flex-col overflow-hidden z-0 -mt-16">
            <div className="h-full bottom-0 bg-gradient-to-b from-white relative z-10" />
            <Mesh className="absolute bottom-0 w-[1140px] min-w-full left-1/2 transform -translate-x-1/2 xl:w-full z-0 opacity-70" />
          </div>
        </div>

        <div className="max-w-3xl mx-auto mb-24 px-4 lg:px-0">
          {firstArticles?.map((article) => (
            <ArticleCard key={article?._uid} article={article} />
          ))}
        </div>

        {!!story?.content?.banner && (
          <div className="w-full max-w-6xl mx-auto px-4 pt-0 relative mb-24">
            <BannerDynamic content={content?.banner?.content} />
          </div>
        )}

        <div className="max-w-3xl mx-auto mb-24 px-4 lg:px-0">
          {articles?.map((article) => (
            <ArticleCard key={article?._uid} article={article} />
          ))}
        </div>
        {articles?.length > 4 && (
          <div className="max-w-3xl mx-auto flex flex-col items-center mb-24">
            <Button
              variant="secondary"
              size="large"
              onClick={loadMore}
              disabled={
                articles?.length >= story?.content?.articles?.length - 3
              }
            >
              Load more articles
            </Button>
          </div>
        )}
      </SbEditable>
    </>
  );
};

export default withLayout(withStory(Press));

export const getStaticProps: GetStaticProps = enhancedStaticProps(
  async ({ sdk }) => {
    let story;
    let notFound = false;

    try {
      story = (await sdk.pressItem({ slug: 'press' })).PressItem;
    } catch (e) {
      notFound = true;
    }

    return {
      props: {
        story,
      },
      notFound: notFound || !story,
      revalidate: 60,
    };
  },
);
